import curiumLogo from '@client/assets/curium-logo.svg';
import { useAppStateStore } from '@client/stores/AppStateStore';
import { isOverlayRoute } from '@client/utils/route-presentation';
import type { Router } from '@tanstack/react-router';
import {
    FALLBACK_NOTIFICATION_TIMEOUT_MS,
    isVersionCheckExcludedPath,
    SILENT_UPDATE_ATTEMPTED_BUILD_KEY,
    SILENT_UPDATE_COOLDOWN_KEY,
    SILENT_UPDATE_COOLDOWN_MS,
    SILENT_UPDATE_OVERLAY_ID,
    UPDATE_RELOAD_GRACE_KEY,
    UPDATE_RELOAD_GRACE_MS,
} from './version-constants';

type SilentUpdateTargetLocation = {
    href: string;
    pathname: string;
    external?: boolean;
};

let isSilentUpdateInFlight = false;

function readSessionStorage(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeSessionStorage(key: string, value: string): void {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        // Ignore storage failures in hardened/private browsing contexts.
    }
}

function removeSessionStorage(key: string): void {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // Ignore storage failures in hardened/private browsing contexts.
    }
}

function hasSilentUpdateCooldownActive(): boolean {
    const marker = readSessionStorage(SILENT_UPDATE_COOLDOWN_KEY);
    if (!marker) {
        return false;
    }

    const timestamp = Number.parseInt(marker, 10);
    if (Number.isNaN(timestamp)) {
        return false;
    }

    return Date.now() - timestamp < SILENT_UPDATE_COOLDOWN_MS;
}

function getAttemptedSilentUpdateBuild(): string | null {
    return readSessionStorage(SILENT_UPDATE_ATTEMPTED_BUILD_KEY);
}

function renderSilentUpdateOverlay(): void {
    if (document.getElementById(SILENT_UPDATE_OVERLAY_ID)) {
        return;
    }

    const overlay = document.createElement('div');
    overlay.id = SILENT_UPDATE_OVERLAY_ID;
    overlay.className = 'curium-loading-container';
    overlay.style.zIndex = '2147483647';
    overlay.innerHTML = `
        <div class="curium-loading-content">
            <img class="curium-loading-logo" src="${curiumLogo}" alt="Curium" />
            <div class="curium-loading-text">Updating Curium</div>
            <div class="curium-progress-bar">
                <div class="curium-progress-fill"></div>
            </div>
            <div class="curium-loading-subtext">Loading the latest version of the application</div>
        </div>
    `;

    document.body.appendChild(overlay);
}

export function clearSilentUpdateSessionMarkers(): void {
    removeSessionStorage(SILENT_UPDATE_COOLDOWN_KEY);
    removeSessionStorage(SILENT_UPDATE_ATTEMPTED_BUILD_KEY);
}

/**
 * Record that an updater-triggered reload ("Reload now" or a silent update) is about
 * to happen. After the reload, `isWithinUpdateReloadGrace` suppresses mismatch handling
 * for UPDATE_RELOAD_GRACE_MS so stale backend version caches can't immediately re-show
 * the "New version available" prompt to a client that just loaded the new bundle.
 */
export function markUpdateReload(): void {
    writeSessionStorage(UPDATE_RELOAD_GRACE_KEY, Date.now().toString());
}

export function isWithinUpdateReloadGrace(): boolean {
    const marker = readSessionStorage(UPDATE_RELOAD_GRACE_KEY);
    if (!marker) {
        return false;
    }

    const timestamp = Number.parseInt(marker, 10);
    if (Number.isNaN(timestamp)) {
        return false;
    }

    return Date.now() - timestamp < UPDATE_RELOAD_GRACE_MS;
}

export function shouldShowFallbackNotification(): boolean {
    const {
        silentUpdatePending,
        silentUpdateDetectedAt,
        silentUpdateBuildNumber,
        versionDismissedBuild,
        versionSnoozeUntil,
    } = useAppStateStore.getState();

    if (!silentUpdatePending || !silentUpdateDetectedAt || !silentUpdateBuildNumber) {
        return false;
    }

    if (versionDismissedBuild === silentUpdateBuildNumber) {
        return false;
    }

    // Honour an active "remind me later" snooze (X-close) — suppress the fallback toast
    // until the snooze window elapses.
    if (versionSnoozeUntil !== null && Date.now() < versionSnoozeUntil) {
        return false;
    }

    return Date.now() - silentUpdateDetectedAt >= FALLBACK_NOTIFICATION_TIMEOUT_MS;
}

export function isSafeToSilentUpdate(
    targetLocation: SilentUpdateTargetLocation,
    router: Router<any, any>,
    options?: { pathChanged?: boolean },
): boolean {
    const appState = useAppStateStore.getState();
    const buildNumber = appState.silentUpdateBuildNumber;

    if (!appState.silentUpdateEnabled || !appState.silentUpdatePending || !buildNumber) {
        return false;
    }

    if (!appState.isOnline) {
        return false;
    }

    if (options?.pathChanged === false || targetLocation.external) {
        return false;
    }

    if (targetLocation.pathname === window.location.pathname) {
        return false;
    }

    if (isVersionCheckExcludedPath(targetLocation.pathname)) {
        return false;
    }

    if (appState.versionDismissedBuild === buildNumber) {
        return false;
    }

    if (getAttemptedSilentUpdateBuild() === buildNumber) {
        return false;
    }

    if (hasSilentUpdateCooldownActive()) {
        return false;
    }

    try {
        const matches = router.matchRoutes(targetLocation.href || targetLocation.pathname, {});
        const lastMatch = matches.at(-1);
        if (lastMatch && isOverlayRoute(lastMatch.staticData)) {
            return false;
        }
    } catch {
        // If route matching fails, keep the other safety checks and allow the reload.
    }

    return true;
}

export function performSilentUpdate(targetHref: string): void {
    if (isSilentUpdateInFlight) {
        return;
    }

    isSilentUpdateInFlight = true;

    const { silentUpdateBuildNumber } = useAppStateStore.getState();
    if (silentUpdateBuildNumber) {
        writeSessionStorage(SILENT_UPDATE_ATTEMPTED_BUILD_KEY, silentUpdateBuildNumber);
    }
    writeSessionStorage(SILENT_UPDATE_COOLDOWN_KEY, Date.now().toString());
    markUpdateReload();

    renderSilentUpdateOverlay();

    try {
        const url = new URL(targetHref, window.location.origin);
        url.searchParams.set('t', Date.now().toString());
        window.location.replace(url.toString());
    } catch {
        window.location.replace(targetHref);
    }
}
